import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Trophy } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import { CP_QUEUE_BUCKETS, type CpQueueController } from '@/hooks/useCpQueue';

/** mm:ss count-up timer. */
function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Human-readable clock label for a preset bucket, e.g. "1:00 + 0:05". */
function bucketLabel(bank: number, inc: number): string {
  const bankMin = Math.floor(bank / 60);
  const bankSec = bank % 60;
  const incMin = Math.floor(inc / 60);
  const incSec = inc % 60;
  const bankStr = bankSec === 0 ? `${bankMin}:00` : `${bankMin}:${bankSec.toString().padStart(2, '0')}`;
  const incStr = incSec === 0 ? `${incMin}:00` : `${incMin}:${incSec.toString().padStart(2, '0')}`;
  return `${bankStr} + ${incStr}`;
}

/** After this long the "widening search" hint appears (cosmetic, matches server ELO window). */
const WIDEN_AFTER_MS = 30_000;

interface CpQueueSheetProps {
  queue: CpQueueController;
  /** Called when the user dismisses the sheet (backdrop tap or Cancel button). */
  onClose: () => void;
  /**
   * Entra nella partita abbinata. Serve perche' l'ingresso puo' NON avvenire
   * (c'e' un'altra partita registrata come attiva e l'utente rifiuta di
   * abbandonarla): in quel caso il tentativo automatico del genitore non
   * naviga, e senza questo l'utente resta abbinato **senza nessun gesto**.
   */
  onEnter: () => void;
}

/**
 * Ranked matchmaking queue bottom sheet for Chinese Poker (OFC).
 *
 * Presents exactly two preset buttons corresponding to the server's
 * `DEFAULT_BUCKETS`:
 *   - Fast     — {bank: 60, inc: 5}  → "1:00 + 0:05"
 *   - Standard — {bank: 120, inc: 10} → "2:00 + 0:10"
 *
 * NO free-form clock picker is provided — the server only pairs players within
 * the same configured bucket, so a custom (bank, inc) pair would enqueue the
 * user but never produce a match. The two buttons are the ONLY allowed emits.
 *
 * While searching (`status === 'searching'`):
 *   - A spinner + count-up timer ("searching… 0:12")
 *   - After 30s, a "widening search" hint (cosmetic — the server actually
 *     expands its ELO window client-independently; we just reflect it visually)
 *   - Cancel button + dim backdrop both call `leave()` and `onClose()`
 *
 * On `mm:matched` the hook only exposes `matched` — it does not navigate.
 * The parent (CpLobby) drives startMatch() from that value, which navigates
 * away and unmounts this sheet.
 *
 * 🔴 E QUANDO QUELL'INGRESSO NON AVVIENE, QUESTO FOGLIO DICEVA UNA COSA FALSA.
 * Il corpo era binario — `status === 'idle' ? bottoni : ricerca` — quindi
 * `status === 'matched'` cadeva nel ramo **ricerca**: spinner e «sto cercando»
 * *mentre il server ti ha GIA' abbinato e sta tenendo una partita vera per te*.
 * E l'orologio si ferma (`status !== 'searching'`), quindi restava un tempo
 * congelato sotto uno spinner che gira.
 * ⇒ Terzo ramo, `matched`, con **l'azione** che mancava: l'utente era abbinato e
 * non aveva niente da premere. *E' la stessa regola dei pannelli della Hub —
 * uno stato senza righe dice QUALE caso e', e non ne afferma un altro.*
 *
 * Hardcoded English strings (T13 extracts locales).
 */
export function CpQueueSheet({ queue, onClose, onEnter }: CpQueueSheetProps) {
  useLivelloIndietro(true, cancel);
  const { t } = useTranslation('chinesepoker');
  const { status, enqueuedAt, leave } = queue;

  const [elapsed, setElapsed] = useState(0);

  // Count-up clock while searching. Anchors on `enqueuedAt` (set at emit time)
  // once available, then ticks every second.
  useEffect(() => {
    if (status !== 'searching') {
      setElapsed(0);
      return;
    }
    const start = enqueuedAt ?? Date.now();
    setElapsed(Date.now() - start);
    const id = window.setInterval(() => setElapsed(Date.now() - start), 1_000);
    return () => window.clearInterval(id);
  }, [status, enqueuedAt]);

  const widened = elapsed >= WIDEN_AFTER_MS;

  function cancel() {
    leave();
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={t('queue.title')}
    >
      {/* Dim backdrop — tap to cancel */}
      <button
        type="button"
        aria-label={t('queue.cancel')}
        onClick={cancel}
        className="absolute inset-0 bg-black/60"
      />

      <div className="relative w-full max-w-md rounded-t-2xl bg-bg-secondary p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        {/* Sheet header */}
        <div className="mb-4 flex items-center gap-2">
          <h2 className="flex items-center gap-2 text-base font-bold text-fg">
            <Trophy className="h-4 w-4 text-brand" />
            {t('queue.title')}
          </h2>
        </div>


        {status === 'matched' ? (
          /* ── abbinato: il server ha una partita per te, e qui c'e' il gesto ── */
          <div className="flex flex-col items-center gap-4 py-4" data-testid="cp-queue-matched">
            <div className="text-center">
              <p className="text-sm font-semibold text-fg">
                {t('queue.matched', { defaultValue: 'Abbinato!' })}
              </p>
              {/* ⛔ Non dice «stai entrando»: se il tentativo automatico fosse
                  andato a buon fine questo foglio non sarebbe piu' montato.
                  Dire «un attimo» qui sarebbe la stessa bugia dello spinner. */}
              <p className="mt-1 text-xs text-fg-muted">
                {t('queue.matchedHint', {
                  defaultValue: 'La partita ti sta aspettando. Se ne hai un\u2019altra in corso, entrando la abbandoni.',
                })}
              </p>
            </div>

            <Button peso="bold" fullWidth onClick={onEnter}>
              {t('queue.matchedEnter', { defaultValue: 'Entra nella partita' })}
            </Button>
            <Button variant="neutro" peso="bold" fullWidth onClick={cancel}>
              {t('queue.matchedLeave', { defaultValue: 'Lascia la coda' })}
            </Button>
          </div>
        ) : status === 'idle' ? (
          /* ── idle: show the two preset bucket buttons ───────────────── */
          <div className="flex flex-col gap-3">
            <p className="text-sm text-fg-muted">
              {t('queue.chooseTime')}
            </p>

            {CP_QUEUE_BUCKETS.map((bucket) => {
              const label =
                bucket.bank === 60 ? t('queue.fast') : t('queue.standard');
              const sublabel = bucketLabel(bucket.bank, bucket.inc);
              return (
                <button
                  key={bucket.bank}
                  type="button"
                  onClick={() => queue.enqueue(bucket)}
                  className="flex w-full items-center justify-between rounded-xl bg-bg px-4 py-3 text-sm font-semibold text-fg transition-opacity active:opacity-80 border border-border hover:border-brand/40"
                >
                  <span>{label}</span>
                  <span className="tabular-nums text-fg-muted">{sublabel}</span>
                </button>
              );
            })}
          </div>
        ) : (
          /* ── searching: spinner + elapsed + widening hint + Cancel ──── */
          <div className="flex flex-col items-center gap-4 py-4">
            <Loader2 className="h-10 w-10 animate-spin text-brand" />

            <div className="text-center">
              <p className="text-sm font-semibold text-fg">
                {widened ? t('queue.widening') : t('queue.searching')}
              </p>
              <p className="mt-1 text-xs tabular-nums text-fg-muted">
                {formatElapsed(elapsed)}
              </p>
              {widened && (
                <p className="mt-1 text-xs text-fg-muted">
                  {t('queue.widenHint')}
                </p>
              )}
            </div>

            <Button variant="neutro" peso="bold" fullWidth onClick={cancel}>
              {t('queue.cancel')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
